<template>
  <Teleport to="body">
    <Transition name="sidebar-fade">
      <div v-if="visible" class="sidebar-overlay" @click="handleClose">
        <Transition name="sidebar-slide">
          <div v-if="visible" class="settings-sidebar" @click.stop>
            <!-- 标题 -->
            <div class="sidebar-header">
              <h2>{{ t('system_setting') }}</h2>
            </div>

            <!-- 菜单列表 -->
            <div class="menu-list">
              <div v-for="item in visibleMenuItems" :key="item.id" class="menu-item" @click="handleMenuClick(item)">
                <img :src="item.icon" :alt="item.text" class="menu-icon" />
                <div class="menu-label">{{ item.text }}</div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { GameMessage } from '@/components/GameMessage'
import { useMenuItems } from '@/config/menuConfig'
import { exitLiveAndRestoreLobby } from '@/composables/useLiveGamePlay'
import { useSettingsSidebar } from '@/composables/useSettingsSidebar'
import { useSettingsDialogs } from '@/composables/useSettingsDialogs'

const { t } = useI18n()
const router = useRouter()

// 使用全局状态控制侧边栏显示
const { visible, close } = useSettingsSidebar()

// 使用全局设置弹窗
const { openDialog } = useSettingsDialogs()

// 全屏状态
const isFullscreen = ref(false)

/**
 * 使用统一菜单配置
 */
const { menuItems } = useMenuItems()

/**
 * 过滤显示的菜单项 (添加全屏和退出登录)
 */
const visibleMenuItems = computed(() => {
  // 先过滤菜单配置中的项目
  const filteredMenus = menuItems.value.filter(item => {
    // display 可能是 boolean 或 function
    return typeof item.display === 'function' ? item.display() : item.display
  })

  // 添加全屏切换菜单项 (iOS 设备不显示)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
  if (!isIOS) {
    filteredMenus.push({
      id: 'fullscreen',
      text: isFullscreen.value ? t('exit_fullscreen') : t('enter_fullscreen'),
      icon: new URL('@/assets/images/home/config_fullscreen.png', import.meta.url).href,
      display: true,
    })
  }

  // 添加横屏/竖屏锁定菜单项 (仅 Android 设备显示，iOS 不支持此 API)
  const isAndroid = /Android/i.test(navigator.userAgent)
  if (isAndroid) {
    // 锁定横屏按钮
    filteredMenus.push({
      id: 'lock-landscape',
      text: t('lock_landscape'),
      icon: new URL('@/assets/images/home/config_fullscreen.png', import.meta.url).href,
      display: true,
    })
    // 锁定竖屏按钮
    filteredMenus.push({
      id: 'lock-portrait',
      text: t('lock_portrait'),
      icon: new URL('@/assets/images/home/config_fullscreen.png', import.meta.url).href,
      display: true,
    })
  }

  // 添加退出登录菜单项
  filteredMenus.push({
    id: 'logout',
    text: t('exit'),
    icon: new URL('@/assets/images/home/config_9_mobile.svg', import.meta.url).href,
    display: true,
  })

  return filteredMenus
})

/**
 * 处理关闭 (点击遮罩)
 */
const handleClose = () => {
  close()
}

/**
 * 菜单点击事件
 */
const handleMenuClick = (item: { id: string; text: string }) => {
  // 特殊处理全屏切换
  if (item.id === 'fullscreen') {
    handleFullscreen()
    return // 全屏切换不关闭侧边栏,让用户看到状态变化
  }

  // 特殊处理锁定横屏
  if (item.id === 'lock-landscape') {
    handleLockOrientation('landscape')
    return
  }

  // 特殊处理锁定竖屏
  if (item.id === 'lock-portrait') {
    handleLockOrientation('portrait')
    return
  }

  // 特殊处理退出登录
  if (item.id === 'logout') {
    handleLogout()
    return
  }

  // 菜单 ID 到弹窗类型的映射
  const dialogMap: Record<string, Parameters<typeof openDialog>[0]> = {
    report: 'report',
    rules: 'rules',
    'custom-bet': 'customBet',
    music: 'bgm',
    'video-line': 'videoLine',
    settings: 'settings',
    command: 'command',
    'change-password': 'changePassword',
    contact: 'contact',
  }

  const dialogType = dialogMap[item.id]
  if (dialogType) {
    openDialog(dialogType)
  }

  // 关闭侧边栏
  close()
}

/**
 * 锁定屏幕方向
 * Screen Orientation API 类型扩展 (标准 API 但 TypeScript 类型定义不完整)
 */
interface ScreenOrientationExtended extends ScreenOrientation {
  lock(orientation: 'landscape' | 'portrait' | 'landscape-primary' | 'portrait-primary'): Promise<void>
  unlock(): void
}

// localStorage key
const ORIENTATION_LOCK_KEY = 'orientation_lock_preference'

/**
 * 保存锁定偏好到 localStorage
 */
const saveOrientationPreference = (orientation: 'landscape' | 'portrait') => {
  try {
    localStorage.setItem(ORIENTATION_LOCK_KEY, orientation)
  } catch (e) {
    console.warn('[SettingsSidebar] 无法保存屏幕方向偏好:', e)
  }
}

/**
 * 从 localStorage 获取锁定偏好
 */
const getOrientationPreference = (): 'landscape' | 'portrait' | null => {
  try {
    const value = localStorage.getItem(ORIENTATION_LOCK_KEY)
    if (value === 'landscape' || value === 'portrait') {
      return value
    }
  } catch (e) {
    console.warn('[SettingsSidebar] 无法读取屏幕方向偏好:', e)
  }
  return null
}

/**
 * 应用保存的屏幕方向锁定 (静默模式，不显示提示)
 */
const applyOrientationLock = async (targetOrientation: 'landscape' | 'portrait') => {
  try {
    const orientation = screen.orientation as ScreenOrientationExtended | undefined
    if (!orientation || typeof orientation.lock !== 'function') {
      return false
    }

    // 锁定方向需要先进入全屏模式
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen()
    }

    await orientation.lock(targetOrientation)
    return true
  } catch (error) {
    console.warn('[SettingsSidebar] 自动应用屏幕方向锁定失败:', error)
    return false
  }
}

/**
 * 等待全屏状态稳定
 * 全屏切换需要时间，等待 fullscreenchange 事件或超时
 */
const waitForFullscreen = (): Promise<void> => {
  return new Promise(resolve => {
    // 如果已经是全屏状态，直接返回
    if (document.fullscreenElement) {
      resolve()
      return
    }

    // 监听 fullscreenchange 事件
    const onFullscreenChange = () => {
      if (document.fullscreenElement) {
        document.removeEventListener('fullscreenchange', onFullscreenChange)
        // 额外等待一小段时间确保全屏完全稳定
        setTimeout(resolve, 100)
      }
    }

    document.addEventListener('fullscreenchange', onFullscreenChange)

    // 超时保护：最多等待 2 秒
    setTimeout(() => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
      resolve()
    }, 2000)
  })
}

const handleLockOrientation = async (targetOrientation: 'landscape' | 'portrait') => {
  try {
    const orientation = screen.orientation as ScreenOrientationExtended | undefined

    // 检查是否支持屏幕方向锁定 API
    if (!orientation || typeof orientation.lock !== 'function') {
      GameMessage.info(t('orientation_not_supported') || '当前浏览器不支持屏幕方向锁定')
      close()
      return
    }

    // 锁定方向需要先进入全屏模式
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen()
      // 等待全屏状态稳定后再锁定方向
      await waitForFullscreen()
    }

    // 锁定方向
    await orientation.lock(targetOrientation)

    // 保存偏好到 localStorage
    saveOrientationPreference(targetOrientation)

    if (targetOrientation === 'landscape') {
      GameMessage.info(t('orientation_locked_landscape') || '已锁定为横屏')
    } else {
      GameMessage.info(t('orientation_locked_portrait') || '已锁定为竖屏')
    }
  } catch (error) {
    console.error('[SettingsSidebar] 屏幕方向锁定失败:', error)
    // 检查是否是桌面浏览器的不支持错误
    if (error instanceof Error && error.name === 'NotSupportedError') {
      GameMessage.info(t('orientation_desktop_not_supported') || '桌面浏览器不支持，请在手机上使用')
    } else {
      GameMessage.info(t('orientation_lock_failed') || '屏幕方向锁定失败')
    }
  }

  close()
}

/**
 * 全屏切换
 */
const handleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement
      .requestFullscreen()
      .then(() => {
        isFullscreen.value = true
        GameMessage.info(t('enter_fullscreen_success') || '已进入全屏')
        // 进入全屏后关闭侧边栏
        close()
      })
      .catch(() => {
        GameMessage.info(t('enter_fullscreen_failed') || '全屏失败')
      })
  } else {
    document.exitFullscreen().then(() => {
      isFullscreen.value = false
      GameMessage.info(t('exit_fullscreen_success') || '已退出全屏')
      // 退出全屏后关闭侧边栏
      close()
    })
  }
}

/**
 * 退出视讯 (回大厅), 不是退出大厅账号
 *
 * 视讯是独立账号体系 (USLO00 WS 登录), 但 memberId/userName/isLoggedIn 这些字段
 * 跟大厅主站共用同一个 userStore —— 绝不能调 userStore.logout()/httpLogout(),
 * 那会把大厅账号也顺带登出了。安全的退出流程统一收在 exitLiveAndRestoreLobby()
 * 里 (断视讯 WS、调退出接口回收资金、用大厅 JWT 纠正被覆盖的字段)。
 */
const handleLogout = async () => {
  try {
    // 关闭侧边栏
    close()

    // 如果是全屏状态，先退出全屏
    if (document.fullscreenElement) {
      await document.exitFullscreen()
      isFullscreen.value = false
    }

    // 清空 sessionStorage 中保存的登录信息 (视讯手动登录的账号密码回填缓存)
    sessionStorage.removeItem('lastLoginUsername')
    sessionStorage.removeItem('lastLoginPassword')

    await exitLiveAndRestoreLobby()
  } catch (error) {
    console.error('[SettingsSidebar] 退出视讯失败:', error)
  }

  // 回大厅首页, 不跳大厅登录页 —— 大厅账号从头到尾都没有登出过
  router.replace('/')
}

/**
 * 监听全屏变化
 */
onMounted(() => {
  // 初始化全屏状态
  isFullscreen.value = !!document.fullscreenElement

  // 监听全屏状态变化
  document.addEventListener('fullscreenchange', () => {
    isFullscreen.value = !!document.fullscreenElement

    // 进入全屏时，自动应用保存的屏幕方向锁定
    if (document.fullscreenElement) {
      const savedOrientation = getOrientationPreference()
      if (savedOrientation) {
        applyOrientationLock(savedOrientation)
      }
    }
  })
})
</script>

<style scoped lang="scss">
// 遮罩层
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
}

// 侧边栏主体
.settings-sidebar {
  // width: 50vw;
  // max-width: 600px;
  height: 100%;
  max-height: 100vh;
  background: rgba(34, 38, 45, 0.95);
  display: flex;
  flex-direction: column;
  color: #fff;
  padding-right: 30px;
  overflow: hidden; // 防止内容溢出，让子元素滚动

  // iPad 安全区域适配 (短边 >= 768px)
  @media (min-width: 768px) {
    height: calc(100% - env(safe-area-inset-top));
    margin-top: env(safe-area-inset-top);
  }

  // 手机横屏
  @media (min-width: 768px) and (max-height: 400px) {
    // width: 60vw;
  }

  // 手机竖屏
  @media (max-width: 767px) {
    // width: 60vw;
  }
}

// 标题
.sidebar-header {
  padding: 30px;
  border-bottom: 1px solid rgba(68, 68, 68, 0.5);

  h2 {
    font-size: 48px;
    font-weight: 600;
    margin: 0;
    color: #fff;

    @media (max-width: 767px) {
      font-size: 36px;
    }
  }
}

// 菜单列表
.menu-list {
  flex: 1;
  min-height: 0; // 关键：让 flex 子元素可以滚动
  overflow-y: auto;
  padding: 0px 0 20px 30px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  padding: 0 30px 0 0;
  transition: all 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &:active {
    background: rgba(255, 255, 255, 0.1);
  }

  .menu-icon {
    width: 72px;
    height: 72px;
    object-fit: contain;
    flex-shrink: 0;

    @media (max-width: 767px) {
      width: 56px;
      height: 56px;
    }
  }

  .menu-label {
    flex: 1;
    font-size: 56px;
    padding: 20px 0;
    border-bottom: 1px solid rgba(68, 68, 68, 1);
    color: #fff;

    @media (max-width: 767px) {
      font-size: 32px;
      padding: 30px 0;
    }
  }

  &:last-child .menu-label {
    border-bottom: none;
  }
}

// 遮罩层淡入淡出动画
.sidebar-fade-enter-active,
.sidebar-fade-leave-active {
  transition: opacity 0.3s ease;
}

.sidebar-fade-enter-from,
.sidebar-fade-leave-to {
  opacity: 0;
}

// 侧边栏滑入滑出动画
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: transform 0.3s ease;
}

.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
  transform: translateX(100%);
}

// 滚动条样式
.menu-list::-webkit-scrollbar {
  width: 8px;
}

.menu-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.menu-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}
</style>
